/**
 * Pricing calculation utilities for dish costing
 * These functions handle all pricing-related calculations including
 * profit margins, selling prices, and cost breakdowns
 */

/**
 * Calculate total cost from raw material, fixed cost, and variable cost
 * @param {number} rawMaterial - Raw material cost
 * @param {number} fixedCost - Fixed cost
 * @param {number} variableCost - Variable cost
 * @returns {number} Total cost
 */
export const totalCost = (rawMaterial, fixedCost, variableCost) => {
  const raw = parseFloat(rawMaterial) || 0;
  const fixed = parseFloat(fixedCost) || 0;
  const variable = parseFloat(variableCost) || 0;
  
  return raw + fixed + variable;
};

/**
 * Calculate selling price from total cost and profit margin
 * @param {number} totalCost - Total cost
 * @param {number} margin - Profit margin (0-0.95, where 0.2 = 20%)
 * @returns {number} Selling price
 * @throws {Error} If margin is >= 1
 */
export const priceFromMargin = (totalCost, margin) => {
  const total = parseFloat(totalCost) || 0;
  const marginValue = parseFloat(margin) || 0;
  
  if (total === 0) return 0;
  
  // Formula: Selling Price = Total Cost / (1 - Margin %/100)
  const result = total / (1 - marginValue);
  
  // Fix precision issue for common cases
  // Round to 2 decimal places to avoid floating point precision issues
  return Math.round(result * 100) / 100;
};

/**
 * Calculate profit margin from total cost and selling price
 * @param {number} totalCost - Total cost
 * @param {number} sellingPrice - Selling price
 * @returns {number} Profit margin (0-1, where 0.2 = 20%)
 * @throws {Error} If selling price is <= 0
 */
export const marginFromPrice = (totalCost, sellingPrice) => {
  const total = parseFloat(totalCost) || 0;
  const price = parseFloat(sellingPrice) || 0;
  
  if (price <= 0) {
    throw new Error("Price must be > 0");
  }
  
  // Formula: Margin % = ((Selling Price - Total Cost) / Selling Price) x 100
  return (price - total) / price;
};

/**
 * Calculate profit cost from total cost and margin
 * @param {number} totalCost - Total cost
 * @param {number} margin - Profit margin (0-1, where 0.2 = 20%)
 * @returns {number} Profit cost
 */
export const profitCostFromMargin = (totalCost, margin) => {
  const total = parseFloat(totalCost) || 0;
  const marginValue = parseFloat(margin) || 0;
  
  if (total === 0) return 0;
  
  // Formula: Profit Cost = (Total Cost x Margin %/100) / (1 - Margin %/100)
  const result = (total * marginValue) / (1 - marginValue);
  
  // Round to 2 decimal places to avoid floating point precision issues
  return Math.round(result * 100) / 100;
};

/**
 * Calculate percentage of selling price for a cost component
 * @param {number} part - Cost component amount
 * @param {number} sellingPrice - Selling price
 * @returns {number} Percentage (0-1, where 0.2 = 20%)
 */
export const percentOfPrice = (part, sellingPrice) => {
  const partValue = parseFloat(part) || 0;
  const price = parseFloat(sellingPrice) || 0;
  
  if (price <= 0) return 0;
  
  return partValue / price;
};



/**
 * Calculate market price comparison
 * @param {number} sellingPrice - Our selling price
 * @param {number} marketPrice - Market price
 * @returns {object} Comparison data with delta and percentage
 */
export const marketPriceComparison = (sellingPrice, marketPrice) => {
  const ourPrice = parseFloat(sellingPrice) || 0;
  const market = parseFloat(marketPrice) || 0;
  
  if (market === 0) {
    return {
      delta: 0,
      percentage: 0,
      isAbove: false,
      isBelow: false,
      isEqual: true
    };
  }
  
  const delta = ourPrice - market;
  const percentage = (delta / market) * 100;
  
  return {
    delta,
    percentage,
    isAbove: delta > 0,
    isBelow: delta < 0,
    isEqual: Math.abs(delta) < 0.01 // Consider equal if difference is less than 1 cent
  };
};

/**
 * Validate profit margin input
 * @param {number} margin - Profit margin to validate
 * @returns {object} Validation result
 */
export const validateMargin = (margin) => {
  const marginValue = parseFloat(margin);
  
  if (isNaN(marginValue)) {
    return {
      isValid: false,
      error: "Please enter a valid number"
    };
  }
  
  if (marginValue > 1000) { // Allow up to 1000% profit margin
    return {
      isValid: false,
      error: "Profit margin cannot exceed 1000%"
    };
  }
  
  return {
    isValid: true,
    error: null
  };
};

/**
 * Validate selling price input
 * @param {number} price - Selling price to validate
 * @returns {object} Validation result
 */
export const validatePrice = (price) => {
  const priceValue = parseFloat(price);
  
  if (isNaN(priceValue)) {
    return {
      isValid: false,
      error: "Please enter a valid number"
    };
  }
  
  if (priceValue <= 0) {
    return {
      isValid: false,
      error: "Selling price must be greater than 0"
    };
  }
  
  return {
    isValid: true,
    error: null
  };
};

/**
 * Check if costs exceed selling price (negative margin)
 * @param {number} totalCost - Total cost
 * @param {number} sellingPrice - Selling price
 * @returns {boolean} True if costs exceed price
 */
export const isNegativeMargin = (totalCost, sellingPrice) => {
  const total = parseFloat(totalCost) || 0;
  const price = parseFloat(sellingPrice) || 0;
  
  return total >= price;
};

/**
 * Format margin as percentage string
 * @param {number} margin - Margin value (0-1)
 * @param {number} decimals - Number of decimal places
 * @returns {string} Formatted percentage
 */
export const formatMargin = (margin, decimals = 1) => {
  const marginValue = parseFloat(margin) || 0;
  const percentage = marginValue * 100;
  
  return `${percentage.toFixed(decimals)}%`;
};

/**
 * Format price difference for market comparison
 * @param {number} delta - Price difference
 * @param {number} percentage - Percentage difference
 * @returns {string} Formatted comparison string
 */
export const formatPriceComparison = (delta, percentage) => {
  const deltaValue = parseFloat(delta) || 0;
  const percentageValue = parseFloat(percentage) || 0;
  
  const sign = deltaValue >= 0 ? '+' : '';
  const deltaFormatted = Math.abs(deltaValue).toFixed(2);
  const percentageFormatted = Math.abs(percentageValue).toFixed(1);
  
  return `${sign}${deltaFormatted} ﷼ (${sign}${percentageFormatted}%)`;
};
